import { useEffect, useState } from "react";
import { apiClient } from "@/lib/auth";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Instagram, Music2, MapPin, FileText, MessageSquare, Hash, Tag, Megaphone,
  PenSquare, Copy, Check, Wand2, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import UsageMeter from "@/components/UsageMeter";

const TYPES = [
  { v: "instagram", l: "Légende Instagram", icon: Instagram, hint: "Engageante, avec hashtags locaux" },
  { v: "tiktok", l: "Légende TikTok", icon: Music2, hint: "Courte et virale" },
  { v: "gbp_post", l: "Post Google Business", icon: MapPin, hint: "Annonce locale, CTA inclus" },
  { v: "seo_description", l: "Description SEO locale", icon: FileText, hint: "Optimisée pour Google" },
  { v: "review_reply", l: "Réponse à un avis", icon: MessageSquare, hint: "Ton humain et professionnel" },
  { v: "hashtags", l: "Hashtags locaux", icon: Hash, hint: "20 hashtags français pertinents" },
  { v: "promo", l: "Offre promotionnelle", icon: Tag, hint: "Urgence + CTA convertissants" },
  { v: "event", l: "Annonce d'événement", icon: Megaphone, hint: "Date, lieu, accroche" },
];

export default function Generator() {
  const [type, setType] = useState("instagram");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("professionnel");
  const [extra, setExtra] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [businessId, setBusinessId] = useState("");
  const [result, setResult] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    apiClient.get("/business").then(r => {
      setBusinesses(r.data);
      if (r.data[0]) setBusinessId(r.data[0].business_id);
    });
    apiClient.get("/ai/history").then(r => setHistory(r.data));
  }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Indiquez un sujet"); return; }
    setLoading(true); setResult("");
    try {
      const r = await apiClient.post("/ai/generate", {
        content_type: type, topic, tone, extra, business_id: businessId || null,
      });
      setResult(r.data.result);
      setHistory([r.data, ...history].slice(0, 50));
      toast.success("Contenu rédigé");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(result);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    toast.success("Copié dans le presse-papier");
  };

  const selectedType = TYPES.find(t => t.v === type);

  return (
    <div className="space-y-8" data-testid="generator-page">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] font-bold text-primary">Studio</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tighter mt-2 font-display">
          Le studio de <span className="font-serif-italic font-normal">contenu</span>
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">
          Rédigez en quelques secondes des contenus en français professionnel, calibrés pour votre activité et votre quartier.
        </p>
      </div>

      <UsageMeter />

      {/* Type selector pills */}
      <div className="flex flex-wrap gap-2">
        {TYPES.map(t => (
          <button key={t.v} onClick={() => setType(t.v)}
            className={`group inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-all border ${
              type === t.v
                ? "bg-gradient-to-r from-primary to-violet-500 text-white border-transparent shadow-sm"
                : "bg-card border-border text-muted-foreground hover:text-foreground hover:border-primary/40"
            }`}
            data-testid={`type-pill-${t.v}`}>
            <t.icon className="h-3.5 w-3.5" />{t.l}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 border border-border rounded-2xl p-6 bg-card space-y-4 h-fit">
          <div className="flex items-center gap-2 pb-3 border-b border-border">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border">
              <selectedType.icon className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm">{selectedType.l}</p>
              <p className="text-xs text-muted-foreground">{selectedType.hint}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Sujet / message principal</Label>
            <Textarea rows={3} value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="Ex : Lancement de notre nouveau brunch dimanche…" className="rounded-md" data-testid="generator-topic" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Ton</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="rounded-md" data-testid="generator-tone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="professionnel">Professionnel</SelectItem>
                  <SelectItem value="chaleureux">Chaleureux</SelectItem>
                  <SelectItem value="dynamique">Dynamique</SelectItem>
                  <SelectItem value="luxe">Premium / Luxe</SelectItem>
                  <SelectItem value="décontracté">Décontracté</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider font-bold">Profil business</Label>
              <Select value={businessId} onValueChange={setBusinessId}>
                <SelectTrigger className="rounded-md" data-testid="generator-business"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  {businesses.map(b => <SelectItem key={b.business_id} value={b.business_id}>{b.business_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-bold">Détails (optionnel)</Label>
            <Input value={extra} onChange={(e) => setExtra(e.target.value)} className="rounded-md" placeholder="Ex : ouvert dimanche, parking gratuit…" data-testid="generator-extra" />
          </div>

          <Button onClick={generate} disabled={loading} className="w-full rounded-full h-11 btn-glow" data-testid="generator-submit-btn">
            <Wand2 className="h-4 w-4 mr-2" />{loading ? "Rédaction…" : "Rédiger le contenu"}
          </Button>
        </div>

        {/* Result */}
        <div className="lg:col-span-3">
          <div className="border border-border rounded-2xl p-6 bg-card min-h-[420px] flex flex-col">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground">Résultat</p>
              {result && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={copy} data-testid="copy-result-btn">
                  {copied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
                  {copied ? "Copié" : "Copier"}
                </Button>
              )}
            </div>
            <div className="flex-1 whitespace-pre-wrap text-[15px] leading-relaxed" data-testid="generator-result">
              {loading && (
                <div className="space-y-3">
                  <div className="h-3 w-3/4 shimmer rounded" />
                  <div className="h-3 w-full shimmer rounded" />
                  <div className="h-3 w-5/6 shimmer rounded" />
                  <div className="h-3 w-2/3 shimmer rounded" />
                </div>
              )}
              {!loading && !result && (
                <div className="h-full flex flex-col items-center justify-center text-center py-12">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary/15 to-violet-500/15 flex items-center justify-center ring-1 ring-border mb-4">
                    <PenSquare className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-semibold">Prêt à rédiger</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs">Indiquez votre sujet et le studio rédige un contenu calibré pour votre commerce.</p>
                </div>
              )}
              {!loading && result && result}
            </div>
          </div>
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-muted-foreground mb-3">Historique récent</p>
          <div className="space-y-2" data-testid="history-list">
            {history.slice(0, 5).map(h => (
              <details key={h.id} className="group border border-border rounded-xl bg-card overflow-hidden">
                <summary className="cursor-pointer p-4 flex items-center gap-3 hover:bg-accent/50 transition-colors">
                  <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
                  <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-bold uppercase tracking-wider">
                    {TYPES.find(t => t.v === h.content_type)?.l || h.content_type}
                  </span>
                  <span className="text-sm truncate flex-1">{h.topic}</span>
                  <span className="text-xs text-muted-foreground hidden sm:block">{new Date(h.created_at).toLocaleDateString("fr-FR")}</span>
                </summary>
                <div className="p-4 pt-0 text-sm whitespace-pre-wrap leading-relaxed border-t border-border">{h.result}</div>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
